import React from "react";
import { motion } from "framer-motion";
import {
  FaCcVisa,
  FaCcMastercard,
  FaCcAmex,
  FaCcDiscover,
} from "react-icons/fa";
import Image from "next/image";
import Link from "next/link";
import Button from "@/components/Micro/Button";
import Skeleton from "@/components/Micro/Skeleton";
import useFetch from "@/hooks/useFetch";
import config from "@/config";

interface PaymentCardType {
  price?: number;
  cardId: string;
  setCardId: (by: string) => void;
  isloading?: boolean;
  handleSubmit?: () => void;
  isPayable?: boolean;
}

const PaymentCard = ({
  price,
  cardId,
  setCardId,
  isloading,
  handleSubmit,
  isPayable = true,
}: PaymentCardType) => {
  const { data: cards, isLoading: isCardLoading } = useFetch(
    config.PAYMENT_BASE_URL + "/cards",
    {
      keepPreviousData: true,
    },
  );

  const getCardIcon = (cardType: string) => {
    switch (cardType.toLowerCase()) {
      case "visa":
        return <FaCcVisa className="text-2xl text-blue-600" />;
      case "mastercard":
        return <FaCcMastercard className="text-2xl text-red-600" />;
      case "amex":
      case "american_express":
        return <FaCcAmex className="text-2xl text-blue-500" />;
      case "discover":
        return <FaCcDiscover className="text-2xl text-orange-600" />;
      default:
        return <FaCcVisa className="text-2xl text-gray-500" />;
    }
  };

  const isCardSelected = (card: any) => cardId === card._id;

  return (
    <div className="space-y-4 bg-white p-4 dark:bg-[#242424]">
      <div className="space-y-3">
        {isCardLoading ? (
          <CardSkeleton />
        ) : cards?.data?.length > 0 ? (
          cards.data.map((card: any) => (
            <div
              key={card._id}
              className={`flex cursor-pointer items-center justify-between border px-4 py-3 transition ${
                isCardSelected(card)
                  ? "border-primary bg-light dark:bg-theme-color-light"
                  : "border-transparent bg-white dark:bg-theme-color-light"
              }`}
              onClick={() => setCardId(card._id)}
            >
              <div className="flex items-center space-x-3">
                {getCardIcon(card.card_type)}
                <span className="text-base font-medium">
                  **** **** **** {card.last4}
                </span>
              </div>
              {isCardSelected(card) && (
                <span className="rounded bg-primary/10 px-2 py-1 text-xs font-semibold text-primary">
                  Selected
                </span>
              )}
            </div>
          ))
        ) : (
          <div className="pb-4 pt-4 text-center">
            <p className="font-semibold text-gray-400">No cards available.</p>
            <Link href="/settings?tab=billing" className="mt-2 text-blue-500">
              Click here to add a card.
            </Link>
          </div>
        )}
      </div>
      {cards?.data?.length > 0 && isPayable ? (
        <div className="my-5 w-full">
          <Button
            className={`w-full ${
              isloading ? "cursor-not-allowed" : "cursor-pointer"
            }`}
            onClick={handleSubmit}
            disabled={isloading}
            loading={isloading}
          >
            Pay ${price}
          </Button>
        </div>
      ) : null}
    </div>
  );
};

export default PaymentCard;

const CardSkeleton = () => (
  <div className="flex w-full items-center justify-between border px-3 py-5 dark:border-theme-color-lighter/40">
    <div className="flex items-center space-x-3">
      <Skeleton className="h-10 w-14 rounded-md dark:!bg-theme-color" />
      <Skeleton className="h-4 w-40 rounded-md dark:!bg-theme-color" />
    </div>
    <Skeleton className="h-5 w-5 rounded-full dark:!bg-theme-color" />
  </div>
);
